import type {
  CreateMeetingRequest,
  InterviewMeta,
  InterviewQuestion,
  InterviewSummary,
  InterviewType,
  Meeting,
} from "@scoach/types";

import { api } from "../../lib/http.ts";

/** Client for the AIMS interview module (gated per-user by interviewAccess). */
export const interviewApi = {
  // Meetings (reuse the generic /meetings endpoints, tagged meetingType="interview").
  create: (body: CreateMeetingRequest) =>
    api<Meeting>("/meetings", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": `iv-${Date.now()}-${Math.random().toString(36).slice(2)}` },
    }),

  fetchMeeting: (id: string) => api<Meeting>(`/meetings/${id}`),

  patchMeeting: (id: string, patch: Partial<Meeting>) =>
    api<Meeting>(`/meetings/${id}`, { method: "PATCH", body: patch }),

  end: (id: string) => api<void>(`/meetings/${id}/end`, { method: "POST", body: {} }),

  // Reference data.
  fetchTypes: () => api<{ types: InterviewType[] }>("/interviews/types").then((r) => r.types ?? []),

  /** All questions for an interview type's tracks (no level/manager filter). */
  fetchAllQuestionsForType: (tracks: string[]) =>
    api<{ questions: InterviewQuestion[] }>("/interviews/questions", {
      query: { track: tracks.join(",") },
    }).then((r) => r.questions ?? []),

  fetchRecommendedQuestions: (q: {
    track?: string;
    level?: string;
    roleType?: string;
    manager?: boolean;
  }) =>
    api<{ questions: InterviewQuestion[] }>("/interviews/questions", {
      query: {
        track: q.track,
        level: q.level,
        roleType: q.roleType,
        manager: q.manager ? "true" : undefined,
      },
    }).then((r) => r.questions ?? []),

  // CV extraction (best-effort).
  uploadCv: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    return api<{ cvContext: string; cvFileName: string }>(`/interviews/${id}/cv`, {
      method: "POST",
      body: fd,
    });
  },

  /** Pre-generate the live summary from the CV (called when the live screen opens). */
  ensureCvSummary: (id: string) =>
    api<{ ok: boolean; existing?: boolean }>(`/interviews/${id}/cv-summary`, {
      method: "POST",
      body: {},
    }),

  suggestQuestions: (
    id: string,
    body: { existingQuestions?: string[]; count?: number; transcriptWindow?: string },
  ) =>
    api<{ questions: string[] }>(`/interviews/${id}/suggest-questions`, {
      method: "POST",
      body,
    }).then((r) => r.questions ?? []),

  // Evaluation summary.
  fetchSummary: (id: string) => api<InterviewSummary>(`/interviews/${id}/summary`),

  saveSummary: (id: string, patch: Partial<InterviewSummary>) =>
    api<InterviewSummary>(`/interviews/${id}/summary`, { method: "PATCH", body: patch }),

  submitSummary: (id: string) =>
    api<InterviewSummary>(`/interviews/${id}/summary/submit`, { method: "POST", body: {} }),
};

export type { InterviewMeta };
